<template>
  <div class="product-card">
    <div class="card-inner">
      <div class="card-inner-container">
        <slot name="handle"></slot>
        <div class="avatar-group">
          <img
            class="avatar"
            :style="{ 'background-image': `url(${image})` }"
            :alt="t('common.products.productImageAlt')"
          />
        </div>
        <div class="product-info">
          <div class="flex w-[150px] items-center justify-between gap-2">
            <UITextSmRegular class="w-full truncate">{{ productName }}</UITextSmRegular>
            <UITag
              v-if="isBumProduct"
              type="warning"
              :closable="false"
              size="small"
              class="text-warning-700"
              :round="true"
              :bordered="false"
              >{{ t("common.payment.product.bumpTag") }}</UITag
            >
          </div>
          <UITextSmRegular class="truncate">{{ price }}</UITextSmRegular>
        </div>
        <div class="flex flex-col items-start justify-center gap-2">
          <div class="icon">
            <Edit02Icon
              class="h-4 w-4 cursor-pointer"
              role="button"
              tabindex="0"
              :aria-label="t('common.payment.product.settingsAria')"
              @click="onProductSettings(productIndex)"
            ></Edit02Icon>
          </div>
          <div class="icon">
            <Trash01Icon
              class="h-4 w-4 cursor-pointer"
              role="button"
              tabindex="0"
              :aria-label="t('common.payment.product.deleteAria')"
              @click="onProductDelete(productIndex)"
            ></Trash01Icon>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Edit02Icon, Trash01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { UITag, UITextSmRegular } from "@gohighlevel/ghl-ui";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useAppStore } from "../../../store/app";
import type { BumProduct, Product } from "../../../types/product";
const { t } = useI18n();

interface Props {
  product: Product | BumProduct;
  productIndex: number;
  isBumProduct: boolean;
}

const props = defineProps<Props>();
const store = useAppStore();

const emits = defineEmits(["product:deleted", "product:settings"]);

const onProductDelete = (id: number) => {
  emits("product:deleted", id);
};

const onProductSettings = (id: number) => {
  emits("product:settings", id);
};

const price = computed(() => {
  const symbol = props.isBumProduct
    ? store.currencyList[(props.product as BumProduct).currency]?.symbol || "$"
    : store.currencyList[(props.product as Product).prices[0].currency]?.symbol || "$";
  return symbol + props.product.amount;
});

const image = computed(() => {
  if (props.isBumProduct) {
    const bumProduct = props.product as BumProduct;
    return bumProduct.uploadedImage || bumProduct.image;
  }
  return (props.product as Product).image;
});

const productName = computed(() => props.product.name);
</script>

<style scoped>
.product-card {
  display: flex;
  padding: 16px 16px 16px 8px;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  transition: background-color 0.2s ease;
}

.product-card:hover {
  background-color: #f9fafb;
}

.card-inner {
  display: flex;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  border-radius: 8px 8px 0px 0px;
}

.card-inner-container {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1 0 0;
  min-width: 0;
}

.avatar-group {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.avatar {
  display: flex;
  width: 56px;
  height: 56px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 200px;
  border: 1px solid #eaecf0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  transition: border-color 0.2s ease;
  flex-shrink: 0;
}

.avatar:hover {
  border-color: #d0d5dd;
}

.product-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 1 0;
  min-width: 0;
  gap: 4px;
}

.icon {
  display: flex;
  padding: 8px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.icon:hover {
  background-color: #f3f4f6;
}

.icon:focus-within {
  outline: 2px solid #155eef;
  outline-offset: 2px;
}

.icon svg {
  transition: color 0.2s ease;
}

.icon:hover svg {
  color: #155eef;
}
</style>
